import React, { useState, useEffect, useRef } from 'react';
import { X, Send, Trash2, MessageCircle } from 'lucide-react';
import { db } from '../../config/firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { useBusiness } from '../../contexts/BusinessContext';
import { Button } from '../ui/Button';
import { Loading } from '../ui/Loading';

interface ChatMessage {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  senderRole: 'user' | 'admin';
  createdAt: Date;
  isRead: boolean;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  profilePhotoUrl?: string;
}

interface AdminChatModalProps {
  user: User;
  isOpen: boolean;
  onClose: () => void;
  onMessagesRead: () => void;
}

const AdminChatModal: React.FC<AdminChatModalProps> = ({ 
  user, 
  isOpen, 
  onClose,
  onMessagesRead 
}) => {
  const { businessId, currentBusiness } = useBusiness();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();
  }, [isOpen]);

  // Escuchar mensajes en tiempo real
  useEffect(() => {
    if (!user?.id || !businessId || !isOpen) {
      setLoading(false);
      return;
    }

    // Consulta simplificada mientras se construye el índice
    const chatQuery = query(
      collection(db, 'chats'),
      where('participantIds', 'array-contains', user.id),
      where('businessId', '==', businessId)
    );

    const unsubscribe = onSnapshot(chatQuery, async (snapshot) => {
      const msgs = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate() || new Date()
      })) as ChatMessage[];
      
      // Ordenar en memoria
      msgs.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
      
      setMessages(msgs);
      setLoading(false);

      // Marcar mensajes del usuario como leídos
      const unreadFromUser = snapshot.docs.filter(
        docSnap => docSnap.data().senderRole === 'user' && !docSnap.data().isRead
      );
      
      if (unreadFromUser.length > 0) {
        for (const msgDoc of unreadFromUser) {
          await updateDoc(doc(db, 'chats', msgDoc.id), { isRead: true });
        }
        onMessagesRead();
      }
    }, (error) => {
      console.error('Error escuchando mensajes:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.id, businessId, isOpen, onMessagesRead]);

  // Enviar mensaje
  const handleSend = async () => {
    if (!newMessage.trim() || !user || !businessId) return;

    setSending(true);
    try {
      await addDoc(collection(db, 'chats'), {
        text: newMessage.trim(),
        senderId: 'admin',
        senderName: currentBusiness?.name || 'Admin',
        senderRole: 'admin',
        businessId,
        participantIds: [user.id],
        isRead: false,
        createdAt: serverTimestamp()
      });

      // Crear notificación para el usuario
      await addDoc(collection(db, 'notifications'), {
        userId: user.id,
        businessId,
        title: 'Nuevo mensaje',
        message: `${currentBusiness?.name || 'Admin'} te ha enviado un mensaje`,
        type: 'info',
        isRead: false,
        createdAt: serverTimestamp()
      });

      setNewMessage('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error enviando mensaje:', error);
    } finally {
      setSending(false);
    }
  };

  // Limpiar chat
  const handleClearChat = async () => {
    if (!confirm('¿Estás seguro de que quieres eliminar todo el historial de chat con este usuario?')) return;

    try {
      const batch = writeBatch(db);
      messages.forEach(msg => {
        batch.delete(doc(db, 'chats', msg.id));
      });
      await batch.commit();
    } catch (error) {
      console.error('Error limpiando chat:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/70 z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-lg sm:h-[600px] bg-gray-900 rounded-2xl z-50 flex flex-col overflow-hidden border border-gray-800 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            {user.profilePhotoUrl ? (
              <img 
                src={user.profilePhotoUrl} 
                alt={user.firstName}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">
                  {user.firstName?.charAt(0)}
                </span>
              </div>
            )}
            <div>
              <h3 className="text-white font-semibold">
                {user.firstName} {user.lastName}
              </h3>
              <p className="text-gray-500 text-sm">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button
                onClick={handleClearChat}
                className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-800 rounded-lg transition-colors"
                title="Limpiar chat"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loading size="lg" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <MessageCircle className="w-8 h-8 text-gray-600" />
              </div>
              <p className="text-gray-500">
                No hay mensajes con este usuario
              </p>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.senderRole === 'admin' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    message.senderRole === 'admin'
                      ? 'bg-primary-600 text-white rounded-br-md'
                      : 'bg-gray-800 text-white rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {message.text}
                  </p>
                  <p className={`text-xs mt-1 ${
                    message.senderRole === 'admin' ? 'text-primary-200' : 'text-gray-500'
                  }`}>
                    {formatTime(message.createdAt)}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-gray-800">
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe un mensaje..."
              className="flex-1 bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={sending}
            />
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() || sending}
              className="p-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-xl text-white transition-colors"
            >
              {sending ? (
                <Loading size="sm" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminChatModal;

